import {Injectable} from '@angular/core';
import {Q9HtmlEncoderPipe} from '@q9elements/ui-kit/common';
import {DiagramUpdateService} from '../../diagram-data/diagram-update.service';
import {EditorRegistryService} from '../../diagram-render/mx-graph/editor/editor-registry.service';
import {GraphHelperService} from '../../diagram-render/helpers/graph-helper.service';
import * as _ from 'lodash';
import {StylesService} from '../../diagram-render/styles/styles.service';
import {ShapeSpawnerService} from '../../diagram-render/shapes/shape-spawner.service';
import {DiagramCanvasService} from '../../diagram-render/diagram-canvas/diagram-canvas.service';
declare const mxUtils: any;
declare let mxConstants: any;

@Injectable()
export class SwimlanesService {
  poolVisible = true;

  poolInvisible: any = {};

  constructor(
    private diagramUpdateSvc: DiagramUpdateService,
    private editorRegistry: EditorRegistryService,
    private graphHelper: GraphHelperService,
    private stylesSvc: StylesService,
    private readonly encodeHtml: Q9HtmlEncoderPipe,
    private shapeSpawnerSvc: ShapeSpawnerService,
    private canvasSvc: DiagramCanvasService
  ) {}

  changeSwimlane(mxKey, updateInfo) {
    const graph = this.editorRegistry.getEditorUI().editor.graph;
    const model = graph.getModel();
    const pool = this.graphHelper.swimlaneParent();

    const action = {
      mxKey: +mxKey
    };

    model.beginUpdate();

    if (updateInfo.text) {
      model.setValue(model.getCell(mxKey), this.encodeHtml.transform(updateInfo.text, true));
      action['text'] = updateInfo.text;
    }

    if (typeof updateInfo.position !== 'undefined') {
      const child = pool.getChildAt(updateInfo.position[0]);

      pool.insert(child, updateInfo.position[1]);
      action['position'] = updateInfo.position[1];
    }

    model.endUpdate();
    graph.layoutManager.layoutCells([pool]);

    this.diagramUpdateSvc.update({changeSwimline: action}, false);
  }

  changeSwimlaneStyle(mxKey, style) {
    const graph = this.editorRegistry.getEditorUI().editor.graph;
    const model = graph.getModel();

    model.beginUpdate();
    this._applyMxStyles(mxKey, style);
    model.endUpdate();

    this.diagramUpdateSvc.update(
      {
        changeSwimline: {
          mxKey: +mxKey,
          style: style
        }
      },
      false
    );
  }

  switchVisible(diagramId) {
    this.poolInvisible[diagramId] = !this.poolInvisible[diagramId];
    this.poolVisible = !this.poolInvisible[diagramId];
    this.changeCanvas();
  }

  private changeCanvas() {
    this.canvasSvc.swimlanesVisible = this.poolVisible;
    const graph = this.editorRegistry.getEditorUI().editor.graph;
    const model = graph.model;

    model.setVisible(this.graphHelper.swimlaneParent(), this.poolVisible);
  }

  setInitialVisibility(diagramId) {
    this.poolVisible = !this.poolInvisible[diagramId];
    this.canvasSvc.swimlanesVisible = this.poolVisible;
  }

  switchFlow(swimlanes, horizontal) {
    if (!swimlanes.length) {
      return;
    }

    const graph = this.editorRegistry.getEditorUI().editor.graph;
    const model = graph.getModel();
    const pool = this.graphHelper.swimlaneParent();

    const group = this.diagramUpdateSvc.group();

    group.add({
      changeSwimlinePool: {
        horizontal: horizontal
      }
    });

    model.beginUpdate();
    model.setStyle(pool, <string>mxUtils.setStyle(pool.style, 'horizontalStack', horizontal));
    swimlanes.forEach(lane => {
      lane.style.horizontal = horizontal;
      this._flipGeo(lane.mxKey, graph);
      this._applyMxStyles(lane.mxKey, lane.style);
      group.add({
        changeSwimline: {
          mxKey: +lane.mxKey,
          size: {w: lane.size.h, h: lane.size.w},
          style: lane.style
        }
      });
    });
    model.endUpdate();
    graph.layoutManager.layoutCells([pool]);

    group.sendGroup(false);
  }

  addSwimlane(horizontal, poolStyle) {
    const style = this.stylesSvc.getPrimary('swimlane');
    const graph = this.editorRegistry.getGraph();

    style.horizontal = horizontal;
    style.borderColor = poolStyle.borderColor;
    const data = {
      text: 'Title',
      size: {
        w: horizontal ? 200 : poolStyle.length,
        h: horizontal ? poolStyle.length : 200
      },
      style: style
    };
    const cell = this.shapeSpawnerSvc.createSwimlane(data);

    graph.addCell(cell, this.graphHelper.swimlaneParent());
    this.diagramUpdateSvc.update(
      {
        addSwimline: {
          mxKey: +cell.id,
          text: data.text,
          size: data.size,
          style: data.style
        }
      },
      false
    );
  }

  createPool() {
    const graph = this.editorRegistry.getEditorUI().editor.graph;
    const model = graph.getModel();
    const pool = this.graphHelper.swimlaneParent();
    const pos = this._calculateStartPoint(graph);

    model.beginUpdate();
    model.setStyle(pool, mxUtils.setStyle(pool.style, 'startX', pos.x));
    model.setStyle(pool, mxUtils.setStyle(pool.style, 'startY', pos.y));
    model.setStyle(pool, mxUtils.setStyle(pool.style, 'horizontalStack', 0));
    model.endUpdate();
    graph.layoutManager.layoutCells([pool]);

    this.diagramUpdateSvc.update(
      {
        createSwimlinePool: {
          horizontal: 0,
          position: {x: pos.x, y: pos.y},
          style: {length: 1000, borderColor: '#00a9f0'}
        }
      },
      false
    );
  }

  removeAll() {
    const graph = this.editorRegistry.getEditorUI().editor.graph;
    const model = graph.getModel();

    const group = this.diagramUpdateSvc.group();

    this.graphHelper.allSwimlanes().forEach(cell => {
      group.add({
        removeSwimline: {mxKey: +cell.id}
      });
    });
    model.beginUpdate();
    graph.removeCells(this.graphHelper.allSwimlanes());
    model.endUpdate();
    group.sendGroup(true);
  }

  removeSwimlane(mxKey) {
    const graph = this.editorRegistry.getEditorUI().editor.graph;
    const model = graph.getModel();

    model.beginUpdate();
    const cell = model.getCell(mxKey);

    graph.removeCells([cell]);
    model.endUpdate();

    this.diagramUpdateSvc.update(
      {
        removeSwimline: {
          mxKey: +mxKey
        }
      },
      false
    );
  }

  changeBorder(color, swimlanes) {
    const graph = this.editorRegistry.getEditorUI().editor.graph;
    const model = graph.getModel();

    const group = this.diagramUpdateSvc.group();

    group.add({
      changeSwimlinePool: {
        style: {
          borderColor: color
        }
      }
    });

    model.beginUpdate();
    swimlanes.forEach(lane => {
      lane.style.borderColor = color;
      this._applyMxStyles(lane.mxKey, lane.style);
      group.add({
        changeSwimline: {
          mxKey: +lane.mxKey,
          style: lane.style
        }
      });
    });
    model.endUpdate();

    group.sendGroup(true);
  }

  changeLength(inc, horizontal, length) {
    const graph = this.editorRegistry.getEditorUI().editor.graph;
    const model = graph.getModel();

    const measure = horizontal ? 'height' : 'width';
    const newLength = parseInt(length, 10) + inc;

    if (newLength < 400) {
      return;
    }

    const group = this.diagramUpdateSvc.group();

    group.add({
      changeSwimlinePool: {
        style: {
          length: newLength
        }
      }
    });

    model.beginUpdate();
    this.graphHelper.allSwimlanes().forEach(cell => {
      const geo = cell.geometry.clone();

      geo[measure] = newLength;
      model.setGeometry(cell, geo);
      group.add({
        changeSwimline: {
          mxKey: +cell.id,
          size: {w: geo.width, h: geo.height}
        }
      });
    });
    model.endUpdate();
    group.sendGroup(true);
  }

  changeSize(inc, horizontal, lane) {
    const graph = this.editorRegistry.getEditorUI().editor.graph;
    const model = graph.getModel();

    const measure = horizontal ? 'width' : 'height';
    const newSize = parseInt(lane.size[measure[0]], 10) + inc;

    if (newSize < 160) {
      return;
    }

    model.beginUpdate();
    const cell = model.getCell(lane.mxKey);
    const geo = cell.geometry.clone();

    geo[measure] = newSize;
    model.setGeometry(cell, geo);
    this.diagramUpdateSvc.update(
      {
        changeSwimline: {
          mxKey: +cell.id,
          size: {w: geo.width, h: geo.height}
        }
      },
      false
    );
    model.endUpdate();
  }

  private _applyMxStyles(mxKey, style) {
    const graph = this.editorRegistry.getEditorUI().editor.graph;

    const cell = _.find(this.graphHelper.allSwimlanes(), innerCell => {
      return +innerCell.id === +mxKey;
    });

    const mxStyles = [
      'strokeColor',
      mxConstants.STYLE_SWIMLANE_FILLCOLOR,
      'fontColor',
      'horizontal'
    ];
    const ourStyles = ['borderColor', 'backgroundColor', 'fontColor', 'horizontal'];

    for (let i = 0; i < mxStyles.length; i++) {
      graph.setCellStyles(mxStyles[i], style[ourStyles[i]], [cell]);
    }

    graph.setCellStyles('fontStyle', this._calcStyle(style), [cell]);
  }

  private _calcStyle(styleSource) {
    const x = styleSource.fontBold ? 1 : 0;
    const y = styleSource.fontItalic ? 2 : 0;
    const z = styleSource.fontUnderline ? 4 : 0;

    return x + y + z;
  }

  private _flipGeo(mxKey, graph) {
    const model = graph.getModel();
    const cell = model.getCell(mxKey);
    const geo = cell.geometry.clone();
    const tmp = geo.width;

    geo.width = geo.height;
    geo.height = tmp;
    model.setGeometry(cell, geo);
  }

  private _calculateStartPoint(graph) {
    const scale = graph.view.scale;
    const trans = graph.view.translate;
    const container = graph.container;
    const point = mxUtils.convertPoint(container, 120, 120);

    point.x = point.x / scale - trans.x;
    point.y = point.y / scale - trans.y;

    return point;
  }
}
